/**
 * H5P grading helpers (EAA-1929).
 *
 * Framework-free on purpose: they touch only an H5P content instance and, at most,
 * a DOM scope element, so they are unit-testable without the H5P web component or a
 * player model. `H5PPlayerUI` wires them to a mounted instance.
 */

/** Blanks `STATE_CHECKING` — matches h5p-blanks `toggleButtonVisibility`. */
const H5P_BLANKS_STATE_CHECKING = 'checking';

/**
 * Read the score off an H5P instance WITHOUT grading it (EAA-1929).
 *
 * `getScore` / `getMaxScore` are the H5P.Question contract, but what they return
 * depends on the type: H5P.Blanks computes from the current input, while
 * H5P.AdvancedBlanks returns `clozeController.currentScore`, which is only filled
 * in by `checkAll()`. So this is the CHEAP path — callers that need a score to
 * exist must first call {@link requestH5pGrade}.
 *
 * `getAnswerGiven` guards the "learner never touched it" case: an untouched
 * question must stay unscored rather than being persisted as 0, otherwise a
 * skipped question is indistinguishable from a wrong one.
 */
export function readH5pScore(instance: unknown): { score: number; max: number } | null {
    const inst = instance as {
        getScore?: () => unknown;
        getMaxScore?: () => unknown;
        getAnswerGiven?: () => unknown;
    };
    if (typeof inst?.getScore !== 'function' || typeof inst?.getMaxScore !== 'function') {
        return null;
    }
    if (typeof inst.getAnswerGiven === 'function') {
        try {
            if (inst.getAnswerGiven() !== true) {
                return null;
            }
        } catch {
            return null;
        }
    }
    try {
        const score = Number(inst.getScore());
        const max = Number(inst.getMaxScore());
        if (!Number.isFinite(score) || !Number.isFinite(max) || max <= 0) {
            return null;
        }
        return { score, max };
    } catch {
        return null;
    }
}

/**
 * Did the learner put anything into this activity? (EAA-1929)
 *
 * This has to be asked BEFORE {@link requestH5pGrade}: grading sets the type's own
 * `answered` flag, so afterwards an untouched question is indistinguishable from an
 * attempted one — and force-submitting then persisted `user_score = 0` for questions
 * the learner never opened, which is a wrong grade, not a missing one.
 *
 * Two signals, because neither alone is enough:
 *   - `getAnswerGiven()` is the type's own flag, but several types only set it once
 *     the learner presses Check — which the host hides.
 *   - the learner-state snapshot (`getCurrentState()`) holds typed/selected values
 *     even when nothing was checked, so it catches exactly that case.
 */
export function hasH5pLearnerInput(instance: unknown, learnerState?: unknown): boolean {
    const inst = instance as { getAnswerGiven?: () => unknown };
    if (typeof inst?.getAnswerGiven === 'function') {
        try {
            if (inst.getAnswerGiven() === true) {
                return true;
            }
        } catch {
            /* fall through to the snapshot */
        }
    }
    return hasMeaningfulLearnerState(learnerState);
}

/** True when a `getCurrentState()` snapshot holds at least one real learner value. */
function hasMeaningfulLearnerState(state: unknown): boolean {
    if (state === null || state === undefined) {
        return false;
    }
    if (typeof state === 'string') {
        return state.trim().length > 0;
    }
    if (typeof state === 'number' || typeof state === 'boolean') {
        return true;
    }
    if (Array.isArray(state)) {
        return state.some((entry) => hasMeaningfulLearnerState(entry));
    }
    if (typeof state === 'object') {
        return Object.values(state as Record<string, unknown>).some((entry) =>
            hasMeaningfulLearnerState(entry),
        );
    }
    return false;
}

/**
 * Grade an H5P instance NOW, as if the learner had pressed "Check" (EAA-1929).
 *
 * Needed because the host hides `.h5p-question-check-answer` whenever the LMS
 * activity has `allow_check` off: the learner then has no way to trigger grading,
 * so without this a fully answered question submits with no score at all.
 *
 * Order matters — each step is the type's own check path, so the type fires its own
 * `triggerXAPIAnswered()` and the score reaches the host through the normal xAPI
 * callback instead of being synthesised here:
 *   1. `onCheckAnswer()`      — H5P.AdvancedBlanks (runs `clozeController.checkAll()`)
 *   2. the check BUTTON       — every H5P.Question type registers `check-answer`;
 *                               hiding it with CSS leaves the node clickable
 *   3. `markResults` + `showEvaluation` — Blanks / MarkTheWords style, then xAPI is
 *                               triggered explicitly since no handler ran. H5P.Blanks'
 *                               real answered-event method is `triggerAnswered()`
 *                               (see h5p-blanks `checkAnswer`); the older
 *                               `triggerXAPIAnswered()` name it never exposed is kept
 *                               too so other types that DO use it are unaffected.
 *   4. `showCheckSolution()`  — H5P.MultiChoice (same call the history-presentation
 *                               helper above already relies on)
 *
 * Returns true when one of those fired. The caller is expected to fall back to
 * {@link readH5pScore} when it returns false.
 */
export function requestH5pGrade(instance: unknown, scope?: ParentNode | null): boolean {
    const inst = instance as {
        onCheckAnswer?: () => void;
        $container?: { get?: (i: number) => unknown; 0?: unknown };
        markResults?: () => void;
        showEvaluation?: () => void;
        toggleButtonVisibility?: (state: string) => void;
        showCheckSolution?: (skipFeedback?: boolean) => void;
        triggerXAPIAnswered?: () => void;
        triggerAnswered?: () => void;
    };
    if (!inst) {
        return false;
    }

    if (typeof inst.onCheckAnswer === 'function') {
        try {
            inst.onCheckAnswer();
            return true;
        } catch {
            /* fall through to the button */
        }
    }

    // SCOPE MATTERS: the web component runs H5P in "div mode", so every instance on
    // the page shares ONE window and document. A document-wide lookup would click the
    // first check button in the DOM — in the multi embed that means grading question 1
    // N times and questions 2..N never. `scope` is the instance's own
    // `<h5p-player>` element (see H5PPlayerUI.requestGrade).
    const container = (inst.$container?.[0] ?? inst.$container?.get?.(0)) as
        | Element
        | undefined;
    const root: ParentNode | null | undefined = container ?? scope;
    const button = root?.querySelector?.(
        '.h5p-question-check-answer',
    ) as HTMLElement | null | undefined;
    if (button) {
        try {
            button.click();
            return true;
        } catch {
            /* fall through to the manual path */
        }
    }

    if (typeof inst.markResults === 'function' && typeof inst.showEvaluation === 'function') {
        try {
            if (typeof inst.toggleButtonVisibility === 'function') {
                inst.toggleButtonVisibility(H5P_BLANKS_STATE_CHECKING);
            }
            inst.markResults();
            inst.showEvaluation();
            // H5P.Blanks fires its scored xAPI statement through `triggerAnswered()`,
            // NOT `triggerXAPIAnswered()` (which it never defined). Call both so Blanks
            // now emits a scored `answered` event while any type relying on the older
            // name keeps working.
            inst.triggerAnswered?.();
            inst.triggerXAPIAnswered?.();
            return true;
        } catch {
            return false;
        }
    }

    if (typeof inst.showCheckSolution === 'function') {
        try {
            inst.showCheckSolution(false);
            inst.triggerAnswered?.();
            inst.triggerXAPIAnswered?.();
            return true;
        } catch {
            return false;
        }
    }

    return false;
}
